import { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState, AppDispatch } from '../store';
import { initializeUser, setUserName, clearUser } from '../store/slices/userSlice';
import api from '../services/api';

export const UserNamePrompt = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { currentUser, loading, error } = useSelector((state: RootState) => state.user);
  const [name, setName] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const initializationAttempted = useRef(false); // Keep track of initialization attempt

  useEffect(() => {
    // Attempt initialization only once per component mount or if not already loading
    if (!initializationAttempted.current && !loading) {
      console.log('[UserNamePrompt] Attempting user initialization.');
      dispatch(initializeUser());
      initializationAttempted.current = true;
    }
  }, [dispatch, loading]); // Rely on loading state to prevent re-dispatch if already in progress

  useEffect(() => {
    // Listen for clearUserState event from API interceptor
    const handleClearUserState = () => {
      console.log('[UserNamePrompt] Clearing user state due to API error');
      dispatch(clearUser());
      initializationAttempted.current = false; // Reset so we can initialize again
    };

    window.addEventListener('clearUserState', handleClearUserState);
    return () => {
      window.removeEventListener('clearUserState', handleClearUserState);
    };
  }, [dispatch]);

  useEffect(() => {
    console.log('[UserNamePrompt] Checking conditions to open prompt:', { loading, currentUserExists: !!currentUser, currentUserName: currentUser?.name });
    // Show prompt only after loading is false (initialization is complete)
    // and if there is no current user (or specifically, no user name).
    if (!loading && initializationAttempted.current && !currentUser?.name) {
      console.log('[UserNamePrompt] Conditions met, opening prompt.');
      setIsOpen(true);
    } else if (!loading && currentUser?.name) {
      // If loading is false and we have a user name, ensure prompt is closed.
      console.log('[UserNamePrompt] User has a name, ensuring prompt is closed.');
      setIsOpen(false);
    }
  }, [loading, currentUser]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (name.trim()) {
      try {
        console.log(`[UserNamePrompt] Submitting name: ${name.trim()}`);
        await dispatch(setUserName(name.trim())).unwrap();
        
        // Check if there's a pending QR code session to create
        const pendingQRCode = localStorage.getItem('pendingQRCode');
        const pendingFromQRCode = localStorage.getItem('pendingFromQRCode');
        
        if (pendingQRCode && pendingFromQRCode === 'true') {
          console.log('[UserNamePrompt] Creating pending session for QR code:', pendingQRCode);
          try {
            // Create the session by calling the QR endpoint with fromQRCode=true
            const tableResponse = await api.get(`/tables/qr/${pendingQRCode}?fromQRCode=true`);
            console.log('[UserNamePrompt] Successfully created session for QR code:', pendingQRCode);
            
            // Store the table ID for the current session
            if (tableResponse.data?.id) {
              localStorage.setItem('tableId', tableResponse.data.id);
              console.log('[UserNamePrompt] Stored table ID:', tableResponse.data.id);
            }
            
            // Clear pending QR code information
            localStorage.removeItem('pendingQRCode');
            localStorage.removeItem('pendingFromQRCode');
          } catch (sessionError) {
            console.error('[UserNamePrompt] Failed to create pending session:', sessionError);
            // Don't fail the user creation, just log the error
          }
        }
        
        setIsOpen(false); // Should be closed by the useEffect above, but good to be explicit
        console.log('[UserNamePrompt] Name submitted successfully.');
      } catch (err) {
        console.error('[UserNamePrompt] Failed to set user name:', err);
      }
    }
  };

  // Do not render the dialog if we are in an initial loading phase for user, 
  // or if the user is successfully loaded and has a name.
  if (loading) { // Don't show prompt during any loading state
      console.log('[UserNamePrompt] Loading in progress, not rendering dialog.');
      return null;
  }
  
  // If initialization has been attempted, loading is false, and we have a user with a name, don't render.
  if (!loading && currentUser?.name) {
      console.log('[UserNamePrompt] User loaded with name, not rendering dialog.');
      return null;
  }

  // Render the dialog if isOpen is true (conditions met in useEffect)
  // This also covers the case where loading is false, initialization attempted, but no currentUser.name
  if (!isOpen) {
    console.log('[UserNamePrompt] isOpen is false, not rendering dialog.');
    return null;
  }

  console.log('[UserNamePrompt] Rendering dialog.');
  return (
    <Dialog open={isOpen} onClose={() => { /* Prevent closing by clicking outside */ }} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>¡Bienvenido!</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Por favor ingresa tu nombre para continuar
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Tu Nombre"
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={!!error} // Show error from userSlice if any during setName
            helperText={error}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={!name.trim() || loading} // Disable if loading (e.g. during setName async call)
          >
            Continuar
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}; 